import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { compressImageIfNeeded } from "@/lib/imageCompress";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, Heart, Minus, Plus, Truck, ShieldCheck, RotateCcw, Ruler, Camera, X, ShieldCheck as VerifiedIcon, Award } from "lucide-react";
import { toast } from "sonner";
import ProductGallery from "@/components/ProductGallery";
import SEO from "@/components/SEO";
import ShareProductButton from "@/components/ShareProductButton";
import RelatedProducts from "@/components/RelatedProducts";
import RecentlyViewed, { trackView } from "@/components/RecentlyViewed";
import PincodeCheck from "@/components/PincodeCheck";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImgUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

// FastAPI 422 responses return `detail` as an array of Pydantic error objects.
// Rendering that array via toast.error() would crash React ("Objects are not valid as a React child").
const errMsg = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  if (typeof d === "object") return d.msg || JSON.stringify(d);
  return String(d);
};

export default function Product() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewPhotos, setReviewPhotos] = useState([]); // list of {url}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eligibility, setEligibility] = useState(null); // {allowed, reason}
  const [lightboxUrl, setLightboxUrl] = useState("");
  const fileInputRef = useRef(null);
  const { addToCart, toggleWishlist, isInWishlist } = useCart();
  const { user } = useAuth();

  const load = useCallback(() => {
    api.get(`/products/${id}`).then((r) => {
      setData(r.data);
      const p = r.data.product;
      trackView(p);
      if (p.has_variants && p.variants?.length) {
        const first = p.variants.find((v) => v.stock > 0) || p.variants[0];
        setSelectedSize(first.size || "");
        setSelectedColor(first.color || "");
      }
    });
  }, [id]);

  useEffect(() => {
    load();
    window.scrollTo(0, 0);
  }, [load]);

  useEffect(() => {
    let active = true;
    let pending = false;
    const refreshStock = async () => {
      if (document.hidden || pending) return;
      pending = true;
      try {
        const response = await api.get(`/products/${id}`);
        if (active) setData(current => current ? { ...current, product: response.data.product } : current);
      } catch { /* Checkout verifies authoritative availability. */ }
      finally { pending = false; }
    };
    const timer = window.setInterval(refreshStock, 15000);
    window.addEventListener("focus", refreshStock);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener("focus", refreshStock); };
  }, [id]);

  // Check review eligibility when we know both the product & the user
  useEffect(() => {
    if (!user || !data?.product?.id) {
      setEligibility(null);
      return;
    }
    api
      .get(`/reviews/can-review/${data.product.id}`)
      .then((r) => setEligibility(r.data))
      .catch(() => setEligibility({ allowed: false, reason: "error" }));
  }, [user, data?.product?.id]);

  // Compute current variant based on selection
  const currentVariant = useMemo(() => {
    if (!data?.product?.has_variants) return null;
    const variants = data.product.variants || [];
    return variants.find(
      (v) => (v.size || "") === (selectedSize || "") && (v.color || "") === (selectedColor || "")
    );
  }, [data, selectedSize, selectedColor]);

  if (!data) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-8 py-24">Loading...</div>;
  }
  const p = data.product;
  const liked = isInWishlist(p.id);
  const hasVariants = p.has_variants && p.variants?.length > 0;
  const stockAvailable = Math.max(0, Math.min(
    Number(p.stock || 0),
    hasVariants ? Number(currentVariant?.stock || 0) : Number(p.stock || 0)
  ));
  const galleryPreview =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
    new URLSearchParams(window.location.search).get("galleryDemo") === "1";
  const previewMedia = [
    { kind: "image", url: p.image, view: "front" },
    { kind: "image", url: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?fm=jpg&q=85&w=940", view: "demo-side" },
    { kind: "image", url: "https://images.unsplash.com/photo-1646979200020-941e1deb2670?fm=jpg&q=85&w=940", view: "demo-detail" },
  ];

  const handleAddToCart = () => {
    if (stockAvailable <= 0) return toast.error("This option is sold out");
    if (!Number.isInteger(qty) || qty < 1 || qty > stockAvailable)
      return toast.error(`Only ${stockAvailable} available`);
    if (hasVariants) {
      if (!currentVariant) return toast.error("Please select an available option");
      if (currentVariant.stock < qty) return toast.error(`Only ${currentVariant.stock} left in stock`);
      addToCart(p.id, qty, currentVariant.sku);
    } else {
      addToCart(p.id, qty);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 5 - reviewPhotos.length;
    if (remaining <= 0) {
      toast.error("Max 5 photos per review");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      for (const raw of files.slice(0, remaining)) {
        const { file: compressed } = await compressImageIfNeeded(raw);
        const fd = new FormData();
        fd.append("file", compressed);
        const { data: res } = await api.post("/uploads/review-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setReviewPhotos((prev) => [...prev, { url: res.url }]);
      }
    } catch (err) {
      toast.error(errMsg(err, "Photo upload failed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    setReviewPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const submitReview = async () => {
    if (!user) return toast.error("Please login to write a review");
    if (!reviewText.trim()) return toast.error("Please write something");
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        product_id: p.id,
        rating: reviewRating,
        title: reviewTitle.trim(),
        comment: reviewText,
        photos: reviewPhotos.map((ph) => ph.url),
      });
      setReviewText("");
      setReviewTitle("");
      setReviewPhotos([]);
      setReviewRating(5);
      toast.success("Review submitted");
      // Re-check eligibility so the form hides (one-per-user)
      setEligibility({ allowed: false, reason: "already_reviewed" });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to submit review"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 md:py-10">
      <SEO
        title={p.name}
        description={(p.description || "").slice(0, 155) || `Buy ${p.name} — handcrafted in India, ships pan-India. GST invoice.`}
        image={p.image}
        type="product"
        price={p.price}
        currency="INR"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": p.name,
          "image": ((p.media || []).filter((m) => m.type !== "video").map((m) => m.url).slice(0, 5)).length > 0
            ? (p.media || []).filter((m) => m.type !== "video").map((m) => m.url).slice(0, 5)
            : [p.image],
          "description": p.description || "",
          "sku": p.id,
          "brand": { "@type": "Brand", "name": "Nityavastra" },
          "category": p.category,
          "aggregateRating": p.review_count > 0 ? {
            "@type": "AggregateRating",
            "ratingValue": (p.rating || 4.5).toFixed(1),
            "reviewCount": p.review_count || 0,
          } : undefined,
          "offers": {
            "@type": "Offer",
            "url": typeof window !== "undefined" ? window.location.href : "",
            "priceCurrency": "INR",
            "price": String(p.price),
            "availability": stockAvailable > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "itemCondition": "https://schema.org/NewCondition",
          },
        }}
      />
      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 md:gap-8 lg:gap-10">
        <div>
          {galleryPreview && (
            <p className="mb-3 rounded-lg bg-[#FFF1D6] px-3 py-2 text-xs font-medium text-[#754B13]">
              Gallery layout preview: these additional sample pictures show other products.
              They are not views or variants of this item.
            </p>
          )}
          <ProductGallery
            media={galleryPreview ? previewMedia : (currentVariant?.media?.length ? currentVariant.media : (p.media || []))}
            fallbackImage={p.image}
            alt={currentVariant?.color ? `${p.name} — ${currentVariant.color}` : p.name}
          />
        </div>

        <div className="rounded-2xl bg-[linear-gradient(145deg,#FFFFFF_0%,#FFFAF2_55%,#F0FAF7_100%)] p-5 sm:p-6 lg:p-7 shadow-[0_18px_45px_-36px_rgba(124,31,48,0.55)]">
          <p className="mb-3 inline-flex rounded-full bg-[#FCE8EC] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8A263B]">
            {p.category.replace("-", " ")}
          </p>
          <h1 className="mb-3 font-serif text-3xl leading-tight text-[#173A3A] sm:text-4xl">
            {p.name}
          </h1>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  className={`h-4 w-4 ${
                    s <= Math.round(p.rating || 4.5)
                      ? "fill-[#D4A03A] text-[#D4A03A]"
                      : "text-[#E7E5E4]"
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-[#78716C]">
              {p.rating || 4.5} · {p.review_count || 0} reviews
            </span>
          </div>

          <div className="flex items-baseline gap-3 mb-5">
            <span className="text-3xl font-bold text-[#08776F] sm:text-[34px]">{formatINR(p.price)}</span>
            {p.old_price && (
              <span className="text-lg text-[#78716C] line-through">
                {formatINR(p.old_price)}
              </span>
            )}
          </div>

          <p className="text-sm sm:text-base text-[#536C69] leading-relaxed mb-5">{p.description}</p>

          {/* Variants */}
          {hasVariants && (
            <div className="mb-6 space-y-4">
              {p.size_options?.length > 0 && (
                <div>
                  <div className="uppercase text-[10px] tracking-[0.2em] text-[#78716C] mb-2">Size</div>
                  <div className="flex flex-wrap gap-2">
                    {p.size_options.map((s) => {
                      const inStock = (p.variants || []).some(
                        (v) => v.size === s && (v.color || "") === (selectedColor || "") && v.stock > 0
                      );
                      const active = selectedSize === s;
                      return (
                        <button
                          key={s}
                          onClick={() => setSelectedSize(s)}
                          disabled={!inStock}
                          data-testid={`size-${s}`}
                          className={`px-4 py-2 text-xs uppercase tracking-wider rounded-full border transition-colors ${
                            active
                              ? "bg-[#2A1508] text-white border-[#2A1508]"
                              : inStock
                              ? "bg-white border-[#E7E5E4] hover:border-[#7C1F30]"
                              : "bg-[#F5F5F4] text-[#78716C] border-[#E7E5E4] line-through cursor-not-allowed"
                          }`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {p.color_options?.length > 0 && (
                <div>
                  <div className="uppercase text-[10px] tracking-[0.2em] text-[#78716C] mb-2">
                    Color {selectedColor && <span className="normal-case text-[#2A1508]/70">· {selectedColor}</span>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {p.color_options.map((c) => {
                      const inStock = (p.variants || []).some(
                        (v) => v.color === c.name && (v.size || "") === (selectedSize || "") && v.stock > 0
                      );
                      const active = selectedColor === c.name;
                      return (
                        <button
                          key={c.name}
                          onClick={() => setSelectedColor(c.name)}
                          disabled={!inStock}
                          data-testid={`color-${c.name}`}
                          title={c.name + (inStock ? "" : " (out of stock)")}
                          className={`w-9 h-9 rounded-full ring-offset-2 transition-all ${
                            active ? "ring-2 ring-[#2A1508]" : "ring-1 ring-[#E7E5E4] hover:ring-[#7C1F30]"
                          } ${!inStock ? "opacity-40 cursor-not-allowed" : ""}`}
                          style={{ background: c.hex || "#888" }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {currentVariant && (
                <div className="text-xs text-[#78716C]">
                  {currentVariant.stock > 0
                    ? currentVariant.stock <= 5
                      ? <span className="text-[#7C1F30] font-medium">Only {currentVariant.stock} left!</span>
                      : `${currentVariant.stock} in stock`
                    : <span className="text-red-600">Out of stock</span>}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-5">
            {(p.tags || []).map((t) => (
              <span key={t} className="rounded-full bg-[#EAF7F3] px-3 py-1 text-xs font-medium text-[#176963]">
                {t}
              </span>
            ))}
          </div>

          {/* Quantity + Actions */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-[#E7E5E4] rounded-full">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-dec"
                className="p-3 hover:bg-[#F5F5F4] rounded-l-full transition-colors"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="px-4 min-w-[3rem] text-center font-medium" data-testid="qty-value">
                {qty}
              </span>
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="qty-inc"
                className="p-3 hover:bg-[#F5F5F4] rounded-r-full transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {stockAvailable <= 0 && (
            <div role="status" className="mb-4 rounded-lg bg-[#7C1F30] px-4 py-3 text-center text-sm font-semibold uppercase tracking-[0.18em] text-[#FAF3E7]">
              {hasVariants ? "Selected variant sold out" : "Sold Out"}
            </div>
          )}
          <div className="flex gap-3 mb-6">
            <Button
              onClick={handleAddToCart}
              disabled={stockAvailable === 0}
              data-testid="add-to-cart-btn"
              className="flex-1 rounded-full bg-[linear-gradient(90deg,#7C1F30,#A63C55)] py-6 text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-[0_10px_24px_-12px_rgba(124,31,48,0.65)] transition-all hover:brightness-110 hover:shadow-[0_14px_28px_-12px_rgba(124,31,48,0.75)] disabled:opacity-50"
            >
              {stockAvailable === 0 ? "Sold Out" : "Add to Cart"}
            </Button>
            <Button
              variant="outline"
              onClick={() => toggleWishlist(p.id)}
              data-testid="wishlist-btn"
              className="rounded-full border-[#2A1508] py-6 px-6"
            >
              <Heart className={`h-4 w-4 ${liked ? "fill-[#7C1F30] text-[#7C1F30]" : ""}`} />
            </Button>
            <ShareProductButton
              product={p}
              className="rounded-full border border-[#2A1508] py-6 px-6 flex items-center justify-center text-[#2A1508] hover:bg-[#F5F5F4] transition-colors"
            />
          </div>

          <PincodeCheck weightKg={(p.dimensions?.weight_g || 500) / 1000} />

          {/* Product Details (dimensions) */}
          {p.dimensions && (p.dimensions.length_cm > 0 || p.dimensions.weight_g > 0) && (
            <div className="pt-6 border-t border-[#E7E5E4] mb-6">
              <div className="uppercase text-[10px] tracking-[0.2em] text-[#78716C] mb-3 flex items-center gap-2">
                <Ruler className="h-3 w-3" /> Product Details
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {p.dimensions.length_cm > 0 && (
                  <div>
                    <div className="text-[#78716C]">Length</div>
                    <div className="font-medium">{p.dimensions.length_cm} cm</div>
                  </div>
                )}
                {p.dimensions.width_cm > 0 && (
                  <div>
                    <div className="text-[#78716C]">Width</div>
                    <div className="font-medium">{p.dimensions.width_cm} cm</div>
                  </div>
                )}
                {p.dimensions.height_cm > 0 && (
                  <div>
                    <div className="text-[#78716C]">Height</div>
                    <div className="font-medium">{p.dimensions.height_cm} cm</div>
                  </div>
                )}
                {p.dimensions.weight_g > 0 && (
                  <div>
                    <div className="text-[#78716C]">Weight</div>
                    <div className="font-medium">{p.dimensions.weight_g} g</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Feature strip */}
          <div className="grid grid-cols-3 gap-2 pt-5 sm:gap-3">
            {[
              { icon: Truck, label: "Free shipping" },
              { icon: ShieldCheck, label: "Secure payment" },
              { icon: RotateCcw, label: "7-day returns" },
            ].map((t) => (
              <div key={t.label} className="rounded-xl bg-[#F3F8F5] px-2 py-3 text-center">
                <t.icon className="mx-auto mb-1.5 h-5 w-5 text-[#0B7770]" />
                <div className="text-xs text-[#78716C]">{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-7 border-t border-[#E7E5E4] pt-6" data-testid="reviews-section">
        <h2 className="font-serif text-2xl mb-4 text-[#173A3A]">Customer Reviews</h2>

        {/* Review form / eligibility banner */}
        {!user && (
          <div className="mb-4 p-3 bg-[#FAF3E7] border border-[#E7E5E4] rounded-lg text-sm text-[#78716C]" data-testid="review-login-hint">
            Please <a href="/login" className="text-[#7C1F30] underline">login</a> to write a review.
          </div>
        )}

        {user && eligibility?.reason === "not_purchased" && (
          <div className="mb-4 p-3 bg-[#FAF3E7] border border-[#E7E5E4] rounded-lg text-sm text-[#78716C]" data-testid="review-not-purchased">
            Only verified buyers can review this product. Purchase it to share your thoughts!
          </div>
        )}

        {user && eligibility?.reason === "already_reviewed" && (
          <div className="mb-4 p-3 bg-[#F5F5F4] border border-[#E7E5E4] rounded-lg text-sm text-[#78716C]" data-testid="review-already">
            Thanks — you've already reviewed this product.
          </div>
        )}

        {user && eligibility?.reason === "banned" && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="review-banned">
            Your account has been restricted from posting reviews. Please contact support.
          </div>
        )}

        {user && eligibility?.allowed && (
          <div className="mb-5 p-5 bg-[#F5F5F4] rounded-lg" data-testid="review-form">
            <p className="font-medium mb-3">Write a review</p>
            <div className="flex gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  onClick={() => setReviewRating(s)}
                  data-testid={`review-star-${s}`}
                >
                  <Star
                    className={`h-6 w-6 ${
                      s <= reviewRating
                        ? "fill-[#D4A03A] text-[#D4A03A]"
                        : "text-[#E7E5E4]"
                    }`}
                  />
                </button>
              ))}
            </div>
            <Input
              value={reviewTitle}
              onChange={(e) => setReviewTitle(e.target.value)}
              placeholder="Title (optional)"
              maxLength={120}
              className="bg-white mb-3"
              data-testid="review-title"
            />
            <Textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your thoughts..."
              className="bg-white mb-3"
              data-testid="review-textarea"
            />

            {/* Photo uploader */}
            <div className="mb-3">
              <div className="flex flex-wrap gap-2 mb-2">
                {reviewPhotos.map((ph, i) => (
                  <div key={i} className="relative group" data-testid={`review-photo-preview-${i}`}>
                    <img
                      src={absoluteImgUrl(ph.url)}
                      alt="review"
                      className="h-20 w-20 object-cover rounded-md border border-[#E7E5E4]"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      data-testid={`remove-photo-${i}`}
                      className="absolute -top-2 -right-2 h-6 w-6 bg-[#2A1508] text-white rounded-full flex items-center justify-center opacity-90 hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {reviewPhotos.length < 5 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="review-add-photo-btn"
                    className="h-20 w-20 border-2 border-dashed border-[#E7E5E4] rounded-md flex flex-col items-center justify-center text-[#78716C] hover:border-[#7C1F30] hover:text-[#7C1F30] transition-colors text-xs"
                  >
                    <Camera className="h-5 w-5 mb-1" />
                    {uploading ? "..." : "Add"}
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="review-photo-input"
              />
              <div className="text-[10px] text-[#78716C]">Up to 5 photos. JPG/PNG/WEBP.</div>
            </div>

            <Button
              onClick={submitReview}
              disabled={submitting || uploading}
              data-testid="submit-review-btn"
              className="bg-[#2A1508] hover:bg-[#3F3F3E] text-white rounded-full disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </Button>
          </div>
        )}

        {/* Reviews list */}
        <div className="space-y-6" data-testid="reviews-list">
          {data.reviews.length === 0 ? (
            <p className="text-[#78716C]" data-testid="reviews-empty">No reviews yet. Be the first!</p>
          ) : (
            data.reviews.map((r) => (
              <div
                key={r.id}
                className={`p-6 border rounded-lg ${r.featured ? "border-[#D4A03A] bg-[#FDFAF3]" : "border-[#E7E5E4]"}`}
                data-testid={`review-${r.id}`}
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium">{r.user_name}</div>
                    {r.verified_buyer && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200"
                        data-testid="verified-badge"
                      >
                        <VerifiedIcon className="h-3 w-3" /> Verified Buyer
                      </span>
                    )}
                    {r.featured && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#D4A03A]/15 text-[#8B6914] border border-[#D4A03A]/40"
                        data-testid="featured-badge"
                      >
                        <Award className="h-3 w-3" /> Featured
                      </span>
                    )}
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`h-3 w-3 ${
                          s <= r.rating ? "fill-[#D4A03A] text-[#D4A03A]" : "text-[#E7E5E4]"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                {r.title && <p className="font-medium text-[#2A1508] mb-1">{r.title}</p>}
                <p className="text-sm text-[#2A1508]/80 whitespace-pre-line">{r.comment}</p>

                {r.photos?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {r.photos.map((url, i) => (
                      <button
                        type="button"
                        key={i}
                        onClick={() => setLightboxUrl(absoluteImgUrl(url))}
                        data-testid={`review-photo-${r.id}-${i}`}
                        className="h-20 w-20 rounded-md overflow-hidden border border-[#E7E5E4] hover:opacity-90"
                      >
                        <img src={absoluteImgUrl(url)} alt="review" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {r.admin_reply && (
                  <div
                    className="mt-4 p-3 bg-[#FAF3E7] border-l-2 border-[#7C1F30] rounded-r-md text-xs"
                    data-testid={`admin-reply-${r.id}`}
                  >
                    <div className="font-medium text-[#7C1F30] mb-1">
                      Nityavastra · {r.admin_reply_by || "Store"}
                    </div>
                    <p className="text-[#2A1508]/80 whitespace-pre-line">{r.admin_reply}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Lightbox for review photos */}
        {lightboxUrl && (
          <div
            onClick={() => setLightboxUrl("")}
            data-testid="review-lightbox"
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          >
            <img src={lightboxUrl} alt="review" className="max-h-full max-w-full rounded-md" />
          </div>
        )}
      </section>

      <RelatedProducts productId={p.id} category={p.category} />
      <RecentlyViewed excludeId={p.id} />
    </div>
  );
}
